/**
 * Keeps the screen on while the player is in a room (a phone dimming in the middle of a
 * race, or while waiting for friends, is very annoying). Browsers drop the lock whenever
 * the page is hidden, so it is requested again when the page becomes visible.
 *
 * Safari grants a page its first lock only right after a tap (later requests then pass),
 * but a returning player's app enters the room on a server message, with no tap. So every
 * tap and key press also asks for the lock while it is wanted and missing; while the lock
 * is held, they do nothing.
 */
import { GESTURE_EVENTS } from '../audio/context';

export interface WakeLockEnvironment {
  /** Visibility and gesture listeners go here. */
  document: Document;
  /** navigator.wakeLock; undefined where the browser has none (iOS before 16.4). */
  wakeLock: WakeLock | undefined;
}

function browserWakeLockEnvironment(): WakeLockEnvironment {
  return { document, wakeLock: 'wakeLock' in navigator ? navigator.wakeLock : undefined };
}

export class ScreenWakeLock {
  private wanted = false;
  private sentinel: WakeLockSentinel | null = null;
  /**
   * Requests on their way: one at a time, but a tap may add one to a request made without a
   * tap (on iOS that one is bound to fail). The events of one tap (pointerup, touchend,
   * click) make one request together.
   */
  private pending = 0;
  private tapPending = false;

  constructor(private readonly env: WakeLockEnvironment = browserWakeLockEnvironment()) {
    const doc = env.document;
    doc.addEventListener('visibilitychange', () => {
      if (this.wanted && doc.visibilityState === 'visible') void this.acquire(false);
    });
    // Synchronously inside the gesture's own handler, as Safari wants it.
    const onGesture = () => {
      if (this.wanted && !this.sentinel) void this.acquire(true);
    };
    for (const type of GESTURE_EVENTS) {
      doc.addEventListener(type, onGesture, { capture: true, passive: true });
    }
  }

  setWanted(wanted: boolean): void {
    this.wanted = wanted;
    if (wanted) void this.acquire(false);
    else void this.release();
  }

  private async acquire(gesture: boolean): Promise<void> {
    const { document: doc, wakeLock } = this.env;
    if (this.sentinel || (gesture ? this.tapPending : this.pending > 0)) return;
    if (!wakeLock || doc.visibilityState !== 'visible') return;
    this.pending++;
    if (gesture) this.tapPending = true;
    try {
      const sentinel = await wakeLock.request('screen');
      // Not wanted any more, or the other request got there first.
      if (!this.wanted || this.sentinel) {
        await sentinel.release();
        return;
      }
      this.sentinel = sentinel;
      sentinel.addEventListener('release', () => {
        if (this.sentinel === sentinel) this.sentinel = null;
      });
    } catch {
      // Refused (no tap yet on iOS, battery saver, unsupported context): the next tap asks
      // again; until then the screen may dim, nothing breaks.
    } finally {
      this.pending--;
      if (gesture) this.tapPending = false;
    }
  }

  private async release(): Promise<void> {
    const sentinel = this.sentinel;
    this.sentinel = null;
    await sentinel?.release().catch(() => undefined);
  }
}
